import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utilities/api';
import PricingDisplay from '../components/PricingDisplay';
import Spinner from '../components/Spinner';

const faqs = [
  {
    question: 'Do I pay before a provider approves treatment?',
    answer: 'No. You can review pricing first, and medication payment is collected only after provider approval.',
  },
  {
    question: 'Are doctor visits included in medication pricing?',
    answer: 'Doctor visits are separate. The initial provider visit is required and is $25. Follow-ups are typically at 3 months, 6 months, then annually.',
  },
  {
    question: 'Can I order multiple months at once?',
    answer: 'The table shows 1, 3, and 6 month totals where supported. Your provider may adjust duration based on safety, dose, and refill timing.',
  },
  {
    question: 'Will my dose automatically increase?',
    answer: 'No. Dose changes depend on your progress, tolerance, side effects, and provider review.',
  },
  {
    question: 'Is lab work always required?',
    answer: 'Not always. Some patients may need a basic lab panel before or during treatment.',
  },
];

function PricingPage() {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    api.getPublicPriceInfo()
      .then(response => {
        if (!isMounted) return;

        if (response?.success && Array.isArray(response.data)) {
          setProducts(response.data);
          setError('');
        } else {
          setProducts([]);
          setError('Medication pricing is unavailable right now. Please check back soon or contact support for current pricing.');
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setProducts([]);
        setError('Medication pricing is unavailable right now. Please check back soon or contact support for current pricing.');
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="w-full">
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-primary mb-3">Weight Management Pricing</h1>
        <p className="text-neutral-dark max-w-3xl">
          Clear dose-based pricing before you create an account. No membership fees, no subscriptions, and medication payment happens only after provider approval.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="border-l-4 border-primary bg-neutral-light p-4 rounded">
          <h2 className="font-semibold text-primary mb-1">Medication</h2>
          <p className="text-sm text-neutral-dark">Dose-based Semaglutide and Tirzepatide pricing is shown below for supported duration options.</p>
        </div>
        <div className="border-l-4 border-secondary bg-neutral-light p-4 rounded">
          <h2 className="font-semibold text-primary mb-1">Doctor Visits</h2>
          <p className="text-sm text-neutral-dark">The initial virtual provider visit is required and is $25. Follow-ups are typically at 3 months, 6 months, then annually.</p>
        </div>
        <div className="border-l-4 border-highlight bg-neutral-light p-4 rounded">
          <h2 className="font-semibold text-primary mb-1">Approval First</h2>
          <p className="text-sm text-neutral-dark">You pay for medication only after your provider reviews and approves the treatment plan.</p>
        </div>
      </div>

      {isLoading && <div className="flex justify-center py-12"><Spinner /></div>}

      {!isLoading && error && (
        <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded mb-8">
          {error}
        </div>
      )}

      {!isLoading && !error && <PricingDisplay products={products} />}

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h2 className="text-xl font-semibold text-primary mb-2">What to Expect</h2>
          <ul className="space-y-2 text-neutral-dark text-sm">
            <li>Medication prices vary by dose and treatment duration.</li>
            <li>Shipping and supplies are included when shown in the approved order.</li>
            <li>Some patients may need basic lab work before or during treatment.</li>
            <li>Refills are reviewed against your progress, side effects, and provider guidance.</li>
          </ul>
        </div>
        <div>
          <h2 className="text-xl font-semibold text-primary mb-2">Dose Changes and Refills</h2>
          <p className="text-neutral-dark text-sm mb-4">
            Dose increases are not automatic. Your provider reviews tolerance, progress, and safety before approving dose changes or refills.
          </p>
          <Link
            to="/register"
            className="inline-flex px-5 py-3 bg-button text-white rounded hover:bg-button-light transition duration-200 font-semibold"
          >
            Start Weight Management Registration
          </Link>
        </div>
      </div>

      <div className="mt-10">
        <h2 className="text-xl font-semibold text-primary mb-4">Pricing FAQ</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {faqs.map(item => (
            <div key={item.question} className="border border-gray-200 rounded p-4">
              <h3 className="font-semibold text-primary mb-2">{item.question}</h3>
              <p className="text-sm text-neutral-dark">{item.answer}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PricingPage;
